import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { TranslationService } from '@core/translation/translation.service';
import { BcmService } from '@modules/bcm/bcm.service';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmBaseFacade } from '@modules/bcm/@core/state-management/bcm-base.facade';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { ThemePalette } from '@angular/material/core';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { filteredListExportHeaderActionBase } from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import { BcmDepartment, BcmDepartmentDto } from '@shared/models/bcm-department';
import { departmentsTableData } from '@modules/bcm/@core/state-management/departments/departments-table-data';
import {
    DepartmentEmployeeAssignmentType,
    DepartmentEmployeeSummary,
    DepartmentEmployeeSummaryDto,
} from '@modules/bcm/departments/department-employee-summary.model';
import { BcmCostCenter } from '@shared/models/bcm-cost-center';

export interface PendingDepartmentEmployeeAssignment {
    employeeId: number;
    assignmentType: DepartmentEmployeeAssignmentType;
}

@Injectable({ providedIn: 'root' })
export class DepartmentsFacade extends BcmBaseFacade<BcmDepartment, BcmDepartmentDto> {

    readonly resourcePath = 'departments';

    get resourceNameSingular(): string {
        return this._tolgee.translate('bcmDepartments.singular');
    }

    get resourceNamePlural(): string {
        return this._tolgee.translate('bcmDepartments.plural');
    }

    readonly resourceNamePluralTranslationKey = 'bcmDepartments.plural';

    readonly resourceNameSingularTranslationKey = 'bcmDepartments.singular';

    readonly addNewButtonTextTranslationKey = 'bcmDepartments.new';

    readonly resourceIconPlural = 'groups';

    readonly resourceIconSingular = 'groups';

    readonly deletePermission = BcmUserPermission.DEPARTMENTS_DELETE;

    readonly readPermission = BcmUserPermission.DEPARTMENTS_READ;

    readonly writePermission = BcmUserPermission.DEPARTMENTS_WRITE;

    readonly hideGenericExcelButton = true;

    readonly headerActions = [
        {
            titleKey: 'table.exportCurrentView',
            icon: 'table_view',
            onClick: () => this.exportTableAsExcel(),
            ...filteredListExportHeaderActionBase(),
        },
    ];

    readonly rowActions = [
        {
            titleKey: 'table.rowActionView',
            icon: 'assignment',
            iconColor: 'accent' as ThemePalette,
            onClick: (department: BcmDepartment) => this._bcmNavigationService.navigate('departments/' + department.id),
        },
        {
            titleKey: 'table.rowActionDelete',
            icon: 'delete',
            iconColor: 'warn' as ThemePalette,
            onClick: (department: BcmDepartment) => super.remove(department, department.name).subscribe(),
        },
    ];

    constructor(appNotificationService: AppNotificationService,
                confirmDialogService: ConfirmDialogService,
                bcmUserSettingsFacade: BcmUserSettingsFacade,
                httpClient: HttpClient,
                bcmService: BcmService,
                bcmNavigationService: BcmNavigationService,
                private _bcmNavigationService: BcmNavigationService,
    ) {
        super(
            BcmDepartment,
            departmentsTableData,
            bcmNavigationService,
            appNotificationService,
            confirmDialogService,
            bcmUserSettingsFacade,
            httpClient,
            bcmService,
        );

        this.rowClickHandler = (department: BcmDepartment) => {
            this._bcmNavigationService.navigate('departments/' + department.id);
        };
    }

    loadDepartmentEmployees(departmentId: number): Observable<DepartmentEmployeeSummary[]> {
        return this.getRaw<DepartmentEmployeeSummaryDto[]>(`${this.resourcePath}/${departmentId}/employees`)
            .pipe(
                map((items) => (items ?? []).map((item) => new DepartmentEmployeeSummary(item))),
            );
    }

    assignDepartmentEmployee(
        departmentId: number,
        employeeId: number,
        assignmentType: DepartmentEmployeeAssignmentType = 'primary',
    ): Observable<DepartmentEmployeeSummary> {
        return this.put(
            `${this.resourcePath}/${departmentId}/employees/${employeeId}`,
            { assignmentType },
        ).pipe(
            map((item) => new DepartmentEmployeeSummary(item as DepartmentEmployeeSummaryDto)),
        );
    }

    removeDepartmentEmployee(departmentId: number, employeeId: number): Observable<void> {
        return this.delete(`${this.resourcePath}/${departmentId}/employees/${employeeId}`).pipe(map(() => undefined));
    }

    buildDepartmentPayloadFromForm(formValue: Record<string, unknown>): Partial<BcmDepartment> {
        const costCenters = (formValue.costCenters as BcmCostCenter[] | undefined) ?? [];

        return {
            name: formValue.name as string,
            description: (formValue.description as string | null | undefined) ?? null,
            bcm_employees_id: (formValue.bcm_employees_id as number | null | undefined) ?? null,
            deputy_bcm_employees_id: (formValue.deputy_bcm_employees_id as number | null | undefined) ?? null,
            costCenters,
        };
    }

    saveDepartmentForm(
        formValue: Record<string, unknown>,
        pendingEmployeeAssignments: PendingDepartmentEmployeeAssignment[] = [],
    ): Observable<BcmDepartment> {
        if (!this._userService.checkPermission(this.writePermission)) {
            this.appNotificationService.showError(
                TranslationService.translate('common.error.noSavePermission', {
                    permission: this.resourceNamePluralDisplay,
                }),
            );
            return throwError(() => new Error('no permission'));
        }

        const selected = this.selected;
        const payload = this.buildDepartmentPayloadFromForm(formValue);

        if (selected?.id) {
            return this.update(selected, payload);
        }

        const createStub = new BcmDepartment({
            name: payload.name,
            description: payload.description,
        } as BcmDepartmentDto);

        const pendingAssignments = pendingEmployeeAssignments.filter(
            pending => Number.isInteger(pending.employeeId) && pending.employeeId > 0,
        );

        return this.add(createStub, new HttpParams(), false).pipe(
            switchMap((created) => {
                const departmentId = created?.id;

                if (!departmentId) {
                    return throwError(() => new Error('department create response missing id'));
                }

                const assignments$ = pendingAssignments.length
                    ? forkJoin(
                        pendingAssignments.map(pending => this.assignDepartmentEmployee(
                            departmentId,
                            pending.employeeId,
                            pending.assignmentType,
                        )),
                    )
                    : of([]);

                return assignments$.pipe(
                    switchMap(() => this.update(created, payload)),
                    tap((savedDepartment) => {
                        this.updateStateFor(savedDepartment);
                        this.bcmNavigationService.navigate([this.resourcePath, savedDepartment.id]);
                    }),
                );
            }),
        );
    }
}
